import { ref, type Ref } from 'vue';
import * as signalR from '@microsoft/signalr';
import { apiClient, AUTH_TOKEN_KEY } from '@/api/client';

/**
 * Hub SignalR alliance : ecoute en temps reel les 4 evenements pousses
 * par le backend (cf Siclop.TheHo.Web.Hubs.AllianceHub) :
 *
 *  - AllianceInvitationReceived : invitation a rejoindre une alliance
 *  - TreatyProposalReceived     : proposition de traite recue
 *  - TreatyAccepted             : alliance partenaire a signe notre traite
 *  - TreatyBroken               : alliance partenaire a rompu un traite actif
 *
 * Pattern d'identite : apres connection, le client appelle
 * `RegisterPlayer(playerUniverseId)` cote hub pour que le serveur
 * l'ajoute au groupe player_{puId}. Tous les pushes ciblent ce groupe.
 *
 * Singleton state global. Init une fois cote AppLayout (apres login)
 * via useAllianceHub().init(playerUniverseId).
 */

export interface AllianceInvitationPayload {
  allianceId: number;
  allianceTag: string;
  allianceName: string;
}
export interface TreatyProposalPayload {
  fromAllianceId: number;
  fromTag: string;
  fromName: string;
  kind: string;
}
export interface TreatyAcceptedPayload {
  treatyId: number;
  partnerTag: string;
  partnerName: string;
  kind: string;
}
export interface TreatyBrokenPayload {
  treatyId: number;
  partnerTag: string;
  partnerName: string;
  kind: string;
  casusBelli?: string | null;
}
export interface MemberJoinedPayload {
  allianceId: number;
  allianceTag: string;
  newMemberPuId: number;
  newMemberLogin: string;
}
export interface MemberLeftPayload {
  allianceId: number;
  allianceTag: string;
  leftMemberPuId: number;
  leftMemberLogin: string;
  wasRank: string;
}
export interface MemberRankChangedPayload {
  allianceId: number;
  allianceTag: string;
  targetPuId: number;
  targetLogin: string;
  newRank: string;
}
export interface AllianceUpdatedPayload {
  allianceId: number;
  allianceTag: string;
}

const connection = ref<signalR.HubConnection | null>(null);
const isConnected = ref(false);
const currentPuId = ref<number | null>(null);

type Unsub = () => void;
type Handler<T> = (payload: T) => void;
const invitationHandlers = new Set<Handler<AllianceInvitationPayload>>();
const proposalHandlers = new Set<Handler<TreatyProposalPayload>>();
const acceptedHandlers = new Set<Handler<TreatyAcceptedPayload>>();
const brokenHandlers = new Set<Handler<TreatyBrokenPayload>>();
const memberJoinedHandlers = new Set<Handler<MemberJoinedPayload>>();
const memberLeftHandlers = new Set<Handler<MemberLeftPayload>>();
const memberRankChangedHandlers = new Set<Handler<MemberRankChangedPayload>>();
const allianceUpdatedHandlers = new Set<Handler<AllianceUpdatedPayload>>();

export function useAllianceHub() {
  async function getAccessToken(): Promise<string> {
    try {
      const base = apiClient.defaults.baseURL ?? '';
      const res = await fetch(`${base}/auth/token`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json() as { accessToken?: string; token?: string };
        if (data.accessToken) return data.accessToken;
        if (data.token) return data.token;
      }
    } catch { /* fallback localStorage */ }
    return localStorage.getItem(AUTH_TOKEN_KEY) ?? '';
  }

  function resolveHubUrl(): string {
    const base = apiClient.defaults.baseURL ?? '';
    const root = base.replace(/\/api\/?$/, '');
    return `${root}/hubs/alliance`;
  }

  async function init(playerUniverseId: number): Promise<void> {
    // Si deja connecte avec la meme PU, no-op. Si PU different, on
    // reconnecte (cas changement d'univers actif).
    if (connection.value?.state === signalR.HubConnectionState.Connected
        && currentPuId.value === playerUniverseId) return;
    if (connection.value) await disconnect();
    currentPuId.value = playerUniverseId;

    const hub = new signalR.HubConnectionBuilder()
      .withUrl(resolveHubUrl(), { accessTokenFactory: () => getAccessToken() })
      .withAutomaticReconnect([0, 2_000, 5_000, 10_000, 30_000])
      .configureLogging(signalR.LogLevel.Warning)
      .build();

    // Wire events -> fanout vers handlers locaux
    hub.on('AllianceInvitationReceived', (p: AllianceInvitationPayload) => {
      for (const h of invitationHandlers) try { h(p); } catch { /* swallow */ }
    });
    hub.on('TreatyProposalReceived', (p: TreatyProposalPayload) => {
      for (const h of proposalHandlers) try { h(p); } catch { /* swallow */ }
    });
    hub.on('TreatyAccepted', (p: TreatyAcceptedPayload) => {
      for (const h of acceptedHandlers) try { h(p); } catch { /* swallow */ }
    });
    hub.on('TreatyBroken', (p: TreatyBrokenPayload) => {
      for (const h of brokenHandlers) try { h(p); } catch { /* swallow */ }
    });
    hub.on('MemberJoined', (p: MemberJoinedPayload) => {
      for (const h of memberJoinedHandlers) try { h(p); } catch { /* swallow */ }
    });
    hub.on('MemberLeft', (p: MemberLeftPayload) => {
      for (const h of memberLeftHandlers) try { h(p); } catch { /* swallow */ }
    });
    hub.on('MemberRankChanged', (p: MemberRankChangedPayload) => {
      for (const h of memberRankChangedHandlers) try { h(p); } catch { /* swallow */ }
    });
    hub.on('AllianceUpdated', (p: AllianceUpdatedPayload) => {
      for (const h of allianceUpdatedHandlers) try { h(p); } catch { /* swallow */ }
    });

    hub.onclose(() => { isConnected.value = false; });
    hub.onreconnected(async () => {
      isConnected.value = true;
      // Reregister apres une reconnection (les groupes sont perdus).
      if (currentPuId.value !== null) {
        try { await hub.invoke('RegisterPlayer', currentPuId.value); } catch { /* ignore */ }
      }
    });

    try {
      await hub.start();
      connection.value = hub;
      isConnected.value = true;
      await hub.invoke('RegisterPlayer', playerUniverseId);
    } catch {
      isConnected.value = false;
    }
  }

  async function disconnect(): Promise<void> {
    if (!connection.value) return;
    try { await connection.value.stop(); } catch { /* ignore */ }
    connection.value = null;
    isConnected.value = false;
    currentPuId.value = null;
  }

  function onAllianceInvitation(cb: Handler<AllianceInvitationPayload>): Unsub {
    invitationHandlers.add(cb);
    return () => invitationHandlers.delete(cb) as unknown as void;
  }
  function onTreatyProposal(cb: Handler<TreatyProposalPayload>): Unsub {
    proposalHandlers.add(cb);
    return () => proposalHandlers.delete(cb) as unknown as void;
  }
  function onTreatyAccepted(cb: Handler<TreatyAcceptedPayload>): Unsub {
    acceptedHandlers.add(cb);
    return () => acceptedHandlers.delete(cb) as unknown as void;
  }
  function onTreatyBroken(cb: Handler<TreatyBrokenPayload>): Unsub {
    brokenHandlers.add(cb);
    return () => brokenHandlers.delete(cb) as unknown as void;
  }
  function onMemberJoined(cb: Handler<MemberJoinedPayload>): Unsub {
    memberJoinedHandlers.add(cb);
    return () => memberJoinedHandlers.delete(cb) as unknown as void;
  }
  function onMemberLeft(cb: Handler<MemberLeftPayload>): Unsub {
    memberLeftHandlers.add(cb);
    return () => memberLeftHandlers.delete(cb) as unknown as void;
  }
  function onMemberRankChanged(cb: Handler<MemberRankChangedPayload>): Unsub {
    memberRankChangedHandlers.add(cb);
    return () => memberRankChangedHandlers.delete(cb) as unknown as void;
  }
  function onAllianceUpdated(cb: Handler<AllianceUpdatedPayload>): Unsub {
    allianceUpdatedHandlers.add(cb);
    return () => allianceUpdatedHandlers.delete(cb) as unknown as void;
  }

  return {
    isConnected: isConnected as Readonly<Ref<boolean>>,
    init,
    disconnect,
    onAllianceInvitation,
    onTreatyProposal,
    onTreatyAccepted,
    onTreatyBroken,
    onMemberJoined,
    onMemberLeft,
    onMemberRankChanged,
    onAllianceUpdated,
  };
}
